import React, { useEffect, useState } from "react"
import { buildSlots, tooHighOdds, TOO_HIGH } from "../../../utils/ballparkLogic"
import { submitBets, endBetting, endGame } from "./ballparkRoom"
import { Screen, BpButton, PlayerDot, ChipToken, OddsBadge, Pill, C, F } from "./BallparkUI"
import { playSound } from "../../../utils/ballparkSounds"

const BetScreen = ({ room, code, playerId, isHost, question, setShowScores }) => {
  const [myBets, setMyBets] = useState(() => {
    const raw = room.bets?.[playerId] || []
    return raw.map((b) => ({
      slot: b.slot,
      primary: b.primary !== undefined ? b.primary : (b.chips || 0),
      stacked: b.stacked || 0,
    }))
  })
  const me = room.players[playerId]
  const players = Object.entries(room.players)
  const slots = buildSlots(room.guesses)
  const overOdds = tooHighOdds(slots)
  const doneCount = Object.keys(room.betsDone || {}).length
  const iAmDone = !!room.betsDone?.[playerId]
  const allDone = doneCount >= players.length

  useEffect(() => {
    if (allDone) endBetting(code)
  }, [allDone, code])

  const totalScore = me.score || 0
  const primaryUsed = myBets.reduce((sum, b) => sum + (b.primary || 0), 0)
  const totalStackedUsed = myBets.reduce((sum, b) => sum + (b.stacked || 0), 0)
  const availableScore = totalScore - totalStackedUsed

  const handleSlotClick = (slotIdx) => {
    if (iAmDone) return
    setMyBets((prev) => {
      const existing = prev.find((b) => b.slot === slotIdx)
      if (existing) {
        if (existing.primary === 1 && primaryUsed < 2) {
          playSound("bet")
          return prev.map((b) =>
            b.slot === slotIdx ? { ...b, primary: 2 } : b
          )
        } else {
          playSound("click")
          const nextPrimary = existing.primary - 1
          if (nextPrimary <= 0) {
            return prev.filter((b) => b.slot !== slotIdx)
          }
          return prev.map((b) =>
            b.slot === slotIdx ? { ...b, primary: nextPrimary } : b
          )
        }
      }
      if (primaryUsed >= 2) return prev
      if (prev.length >= 2) return prev
      playSound("bet")
      return [...prev, { slot: slotIdx, primary: 1, stacked: 0 }]
    })
  }

  const slotRow = (slot, idx, isOver) => {
    const isOverSlot = isOver ? TOO_HIGH : idx
    const bet = myBets.find((b) => b.slot === isOverSlot)
    const primary = bet ? bet.primary : 0
    const stacked = bet ? bet.stacked || 0 : 0
    const highlighted = primary > 0
    const owner = isOver ? null : players.find(([pid]) => pid === slot.playerIds[0])

    return (
      <div
        key={isOver ? "over" : idx}
        onClick={() => handleSlotClick(isOverSlot)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          background: highlighted ? C.highlight : isOver ? C.panel : C.card,
          border: highlighted
            ? `2.5px solid ${C.gold}`
            : isOver
            ? `1.5px dashed ${C.tan}`
            : `1.5px solid ${C.border}`,
          borderRadius: 14,
          padding: "10px 14px",
          boxShadow: highlighted ? `0 3px 0 ${C.gold}` : "none",
          cursor: iAmDone ? "default" : "pointer",
          transition: "all 0.15s ease",
        }}
      >
        <OddsBadge odds={isOver ? overOdds : slot.odds} />
        {isOver ? (
          <span style={{ fontWeight: 800, fontSize: 15, color: "#6B5B45" }}>
            Everyone guessed too high
          </span>
        ) : (
          <span style={{ fontFamily: F.display, fontWeight: 800, fontSize: 22 }}>
            {slot.value}
          </span>
        )}

        {primary > 0 && (
          <span style={{ display: "flex", gap: 4, alignItems: "center" }}>
            {Array.from({ length: primary }).map((_, i) => (
              <ChipToken key={i} color={me.color} size={26} />
            ))}
          </span>
        )}

        {highlighted && !iAmDone && (totalScore > 0 || stacked > 0) && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginLeft: "auto",
              background: C.panel,
              borderRadius: 8,
              padding: "4px 8px",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <span style={{ fontSize: 11, fontWeight: 800, color: C.muted }}>STACK:</span>
            <button
              disabled={stacked === 0}
              onClick={() => {
                playSound("click")
                setMyBets((prev) =>
                  prev
                    .map((b) =>
                      b.slot === isOverSlot ? { ...b, stacked: b.stacked - 1 } : b
                    )
                    .filter((b) => b.primary > 0 || b.stacked > 0)
                )
              }}
              style={{
                width: 24,
                height: 24,
                borderRadius: 6,
                border: "none",
                background: C.card,
                color: C.ink,
                fontWeight: 800,
                cursor: stacked > 0 ? "pointer" : "default",
                opacity: stacked > 0 ? 1 : 0.4,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              -
            </button>
            <span style={{ fontFamily: F.display, fontWeight: 800, fontSize: 15, minWidth: 16, textAlign: "center" }}>
              {stacked}
            </span>
            <button
              disabled={availableScore <= 0}
              onClick={() => {
                playSound("bet")
                setMyBets((prev) =>
                  prev.map((b) =>
                    b.slot === isOverSlot ? { ...b, stacked: (b.stacked || 0) + 1 } : b
                  )
                )
              }}
              style={{
                width: 24,
                height: 24,
                borderRadius: 6,
                border: "none",
                background: C.card,
                color: C.ink,
                fontWeight: 800,
                cursor: availableScore > 0 ? "pointer" : "default",
                opacity: availableScore > 0 ? 1 : 0.4,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              +
            </button>
          </div>
        )}

        {highlighted && iAmDone && stacked > 0 && (
          <div
            style={{
              marginLeft: "auto",
              background: C.panel,
              borderRadius: 8,
              padding: "4px 8px",
              fontSize: 12,
              fontWeight: 800,
              color: C.ink,
            }}
          >
            +{stacked} stacked
          </div>
        )}

        {!isOver && owner && !highlighted && (
          <span
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12,
              fontWeight: 700,
              color: slot.playerIds.includes(playerId) ? C.rust : C.muted,
            }}
          >
            <PlayerDot color={owner[1].color} size={20} />
            {slot.playerIds.includes(playerId) ? "You" : owner[1].name}
            {slot.playerIds.length > 1 && ` +${slot.playerIds.length - 1}`}
          </span>
        )}
      </div>
    )
  }

  return (
    <Screen style={{ gap: 10 }}>
      {/* Top Header Row with Code, Round, and Standings Button */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Pill>{code}</Pill>
          <span style={{ fontSize: 13, fontWeight: 700, color: C.muted }}>
            Round {room.round} of {room.totalRounds}
          </span>
        </div>
        <button
          onClick={() => {
            playSound("click")
            setShowScores(true)
          }}
          style={{
            background: C.panel,
            border: `1.5px solid ${C.borderDark}`,
            borderRadius: 12,
            padding: "4px 10px",
            fontFamily: F.display,
            fontWeight: 800,
            fontSize: 12,
            color: C.ink,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          🏆 Scores
        </button>
      </div>

      {/* Compact Question Display */}
      {question && (
        <div
          style={{
            background: C.card,
            border: `1.5px solid ${C.border}`,
            borderRadius: 16,
            padding: "12px 14px",
            fontSize: 14,
            fontWeight: 700,
            lineHeight: 1.3,
            color: C.ink,
            boxShadow: `0 2px 0 ${C.border}`,
            marginTop: 4,
          }}
        >
          <span
            style={{
              background: C.gold,
              color: C.ink,
              fontWeight: 800,
              fontSize: 10,
              padding: "2px 6px",
              borderRadius: 4,
              marginRight: 8,
              letterSpacing: 0.5,
              textTransform: "uppercase",
            }}
          >
            {question.category}
          </span>
          {question.question}
        </div>
      )}

      {/* Place your bets header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 6 }}>
        <div style={{ fontFamily: F.display, fontWeight: 800, fontSize: 20 }}>
          Place your bets
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.muted }}>
          {doneCount} of {players.length} done
        </div>
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: C.muted }}>
        Tap a guess to drop a chip on it. Long shots pay more.
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1, marginTop: 8 }}>
        {slotRow(null, null, true)}
        {slots.map((slot, idx) => slotRow(slot, idx, false))}
      </div>

      <div
        style={{
          background: C.panel,
          borderRadius: 18,
          padding: "16px 18px",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: "#6B5B45" }}>Primary Tokens</span>
              <span style={{ fontSize: 10, fontWeight: 700, color: C.muted }}>Never lost</span>
            </div>
            <span style={{ display: "flex", gap: 6 }}>
              {Array.from({ length: 2 }).map((_, i) => (
                <ChipToken key={i} color={me.color} used={i < primaryUsed} />
              ))}
            </span>
          </div>

          {totalScore > 0 && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: "#6B5B45" }}>Winnings Avail.</span>
              <span style={{ fontSize: 12, fontWeight: 800, color: C.rust }}>{availableScore} / {totalScore} pts</span>
            </div>
          )}
        </div>

        <BpButton
          onClick={() => {
            playSound("click")
            submitBets(code, playerId, myBets)
          }}
          disabled={iAmDone || primaryUsed === 0}
          style={{ width: "100%", height: 48, fontSize: 16 }}
        >
          {iAmDone ? "Waiting for others…" : "Done betting"}
        </BpButton>
      </div>

      {isHost && (
        <BpButton
          variant="gold"
          onClick={() => {
            playSound("click")
            endBetting(code)
          }}
        >
          End betting → reveal
        </BpButton>
      )}
      {isHost && (
        <BpButton
          variant="ghost"
          style={{ height: 44, fontSize: 13 }}
          onClick={() => {
            playSound("click")
            endGame(code)
          }}
        >
          End game
        </BpButton>
      )}
    </Screen>
  )
}

export default BetScreen
